import React, { useState } from 'react';
import PayrollManagement from '../payroll-management.tsx';
import { PayrollProvider } from '../contexts/PayrollContext.tsx';
import { useAppContext } from '../contexts/AppContext.tsx';
import { format, addMonths, subMonths } from 'date-fns';
import { ko } from 'date-fns/locale';

const PayrollPage: React.FC = () => {
  const { isLoading } = useAppContext();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // 🔒 미래 월 상한: 실제 현재 월 + 1까지만 (다음 달 미리 준비 실익은 보존, 그 너머 오입력 구간 차단)
  //    상한은 클릭 시점마다 동적 계산 — 자정을 넘겨 달이 바뀌면 자동으로 한 달 더 열림
  const maxYearMonth = () => format(addMonths(new Date(), 1), 'yyyy-MM');
  const isAtFutureCap = format(currentDate, 'yyyy-MM') >= maxYearMonth();

  // 날짜 네비게이션
  const handlePreviousMonth = () => {
    const newDate = subMonths(currentDate, 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => {
      const next = addMonths(prev, 1);
      return format(next, 'yyyy-MM') <= maxYearMonth() ? next : prev;
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-semibold text-gray-800 mb-4 md:mb-0">급여 관리</h1>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handlePreviousMonth}
            aria-label="이전 달"
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-lg font-medium text-gray-700">
            {format(currentDate, 'yyyy년 MM월', { locale: ko })}
          </span>
          <button
            onClick={handleNextMonth}
            disabled={isAtFutureCap}
            aria-label="다음 달"
            title={isAtFutureCap ? '다음 달까지만 조회할 수 있습니다' : undefined}
            className={`p-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isAtFutureCap ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100'}`}
          >
            <svg className={`w-5 h-5 ${isAtFutureCap ? 'text-gray-400' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <PayrollProvider>
          <PayrollManagement selectedMonth={format(currentDate, 'yyyy-MM')} />
        </PayrollProvider>
      </div>
    </div>
  );
};

export default PayrollPage;
